// One source → target attribute row. A headers attribute on either end turns
// into two controls: the attribute itself plus the key inside it, which is what
// the dot-notation attribute name encodes.

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Select, SelectMenu } from '@decisiv/ui-components';
import { t } from '@lingui/macro';

import { parseAttributeKey } from '../../../features/dynamic-form/core/attributeKey';
import {
  headerKeysShape,
  mappingShape,
  optionShape,
  schemaMapShape,
} from './formPropTypes';
import {
  ArrowLabel,
  AttrName,
  KeySeparator,
  MappingRow,
  MappingSelectWrap,
  SourceKeyCell,
} from '../style';

const HEADERS_TAG = 'object_input';

const toOptions = (keys) =>
  keys.map((key) => ({ id: key, label: key, value: key }));

// Keys already picked for the same headers attribute on other rows stay
// offerable even before the backend has ever seen them.
const targetKeyOptionsFor = (pairMappings, baseName, storedKeys) =>
  toOptions([
    ...new Set([
      ...pairMappings
        .map((other) => parseAttributeKey(other.to_attribute_name || ''))
        .filter((p) => p.baseName === baseName && p.subKey !== null)
        .map((p) => p.subKey),
      ...storedKeys,
    ]),
  ]);

const MappingEntryRow = ({
  mapping,
  pairMappings,
  targetOptions,
  sourceSchemaMap,
  targetSchemaMap,
  sourceIntegrationId,
  targetIntegrationId,
  headerKeysByAttr,
  onChange,
}) => {
  const { rowIdx } = mapping;

  // A target can only be claimed once per pair. The comparison is on the full
  // attribute name, so once a header key is picked the base attribute stays
  // selectable for other rows — 'headers1.A' does not exclude 'headers1'.
  const usedByOthers = new Set(
    pairMappings
      .filter((other) => other.rowIdx !== rowIdx && other.to_attribute_name)
      .map((other) => other.to_attribute_name),
  );
  const availableOptions = [
    { id: '', label: t`— no mapping —`, value: '' },
    ...targetOptions.filter((opt) => !usedByOthers.has(opt.value)),
  ];

  const { baseName, subKey } = parseAttributeKey(
    mapping.to_attribute_name || '',
  );
  const { baseName: sourceBase, subKey: sourceSubKey } = parseAttributeKey(
    mapping.from_attribute_name || '',
  );

  const isHeadersTarget = targetSchemaMap[baseName]?.html_tag === HEADERS_TAG;
  const isSourceHeaders = sourceSchemaMap[sourceBase]?.html_tag === HEADERS_TAG;
  // Only one side gets a key picker: headers-to-headers maps whole objects.
  const showTargetKeyPicker = isHeadersTarget && !isSourceHeaders;
  const showSourceKeyPicker = isSourceHeaders && !isHeadersTarget && !!baseName;

  const sourceCol = showSourceKeyPicker ? (
    <SourceKeyCell>
      <AttrName>{sourceBase}</AttrName>
      <KeySeparator>·</KeySeparator>
      <SelectMenu
        label={t`Key`}
        noneSelectedLabel={t`Pick key`}
        name={`mapping-source-key-${rowIdx}`}
        options={toOptions(
          headerKeysByAttr[`${sourceIntegrationId}.${sourceBase}`] || [],
        )}
        // Omit `selected` until a key is actually chosen so the menu stays
        // uncontrolled and keeps its own auto-picked default.
        {...(sourceSubKey ? { selected: sourceSubKey } : {})}
        onChange={(selectedId) =>
          onChange(
            rowIdx,
            'from_attribute_name',
            selectedId ? `${sourceBase}.${selectedId}` : sourceBase,
          )
        }
      />
    </SourceKeyCell>
  ) : (
    <AttrName>{mapping.from_attribute_name}</AttrName>
  );

  const targetKeyOptions = showTargetKeyPicker
    ? targetKeyOptionsFor(
        pairMappings,
        baseName,
        headerKeysByAttr[`${targetIntegrationId}.${baseName}`] || [],
      )
    : [];

  const fourthCol = showTargetKeyPicker ? (
    <MappingSelectWrap>
      <Select
        label={t`Header key`}
        name={`mapping-header-key-${rowIdx}`}
        options={targetKeyOptions}
        value={subKey || undefined}
        disabled={targetKeyOptions.length === 0}
        // Under createRoot, KDS's document-level click-outside listener
        // sees the dropdown already open and closes it on the same click.
        onClick={(e) => e.stopPropagation()}
        onChange={(selectedId) =>
          onChange(
            rowIdx,
            'to_attribute_name',
            selectedId ? `${baseName}.${selectedId}` : baseName,
          )
        }
      />
    </MappingSelectWrap>
  ) : (
    <div />
  );

  return (
    <MappingRow>
      {sourceCol}
      <ArrowLabel>→</ArrowLabel>
      <MappingSelectWrap>
        <SelectMenu
          label={t`Target attribute`}
          name={`mapping-to-${rowIdx}`}
          options={availableOptions}
          value={baseName}
          selected={baseName}
          noneSelectedLabel={t`— no mapping —`}
          onChange={(value) =>
            onChange(rowIdx, 'to_attribute_name', value || '')
          }
        />
      </MappingSelectWrap>
      {fourthCol}
    </MappingRow>
  );
};

MappingEntryRow.propTypes = {
  mapping: mappingShape.isRequired,
  pairMappings: PropTypes.arrayOf(mappingShape).isRequired,
  targetOptions: PropTypes.arrayOf(optionShape).isRequired,
  sourceSchemaMap: schemaMapShape.isRequired,
  targetSchemaMap: schemaMapShape.isRequired,
  sourceIntegrationId: PropTypes.string.isRequired,
  targetIntegrationId: PropTypes.string.isRequired,
  headerKeysByAttr: headerKeysShape.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default memo(MappingEntryRow);
